"use client";

import { ReactNode, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { usePathname } from "@/i18n/navigation";
import { getQueryClient } from "@/utils/get-query-client";

let posthog_initialized = false;

function init_public_posthog() {
	if (posthog_initialized || typeof window === "undefined") return;

	const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
	const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
	if (!key || !host) return;

	posthog.init(key, {
		api_host: host,
		ui_host: "https://us.posthog.com",
		defaults: "2026-01-30",
		capture_pageview: false,
		capture_pageleave: true,
		custom_campaign_params: ["ref"]
	});
	posthog_initialized = true;
}

export function PublicProviders({ children }: { children: ReactNode }) {
	init_public_posthog();
	const query_client = getQueryClient();

	return (
		<QueryClientProvider client={query_client}>
			<PostHogProvider client={posthog}>{children}</PostHogProvider>
		</QueryClientProvider>
	);
}

export function PublicPostHogPageView(): null {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const posthog_client = usePostHog();

	useEffect(() => {
		if (!pathname || !posthog_initialized) return;

		let url = window.origin + pathname;
		if (searchParams.toString()) url = `${url}?${searchParams.toString()}`;

		posthog_client.capture("$pageview", {
			$current_url: url
		});
	}, [pathname, posthog_client, searchParams]);

	return null;
}
